import { useCallback } from 'react';

import { useApolloClient } from '@apollo/client';
import { useGetVariantContentOptions } from 'features/menu/hooks/use-get-variant-content-options';

import { MenuObjectTypes } from 'enums/menu';
import {
  IGetComboPlusQuery,
  IGetItemPlusQuery,
  IGetPickerPlusQuery,
  useGetComboPlusLazyQuery,
  useGetItemPlusLazyQuery,
} from 'generated/sanity-graphql';
import usePosVendor from 'hooks/menu/use-pos-vendor';
import { withLazySimpleCache } from 'state/graphql/with-simple-cache';
import { IMenuObject } from 'state/menu/types';
import { useSanityGqlEndpoint } from 'state/network';
import { logger } from 'utils/logger';

import { buildVendorVariables } from './build-vendor-variables';
import { getValidDataFromQuery } from './get-valid-data-from-query';
import { IGetPickerPlusByVendorQuery } from './picker-batched-query-types';
import { fetchPickerBatchedAsync } from './use-picker-batched-query';

const useComboQuery = withLazySimpleCache(useGetComboPlusLazyQuery);
const useItemQuery = withLazySimpleCache(useGetItemPlusLazyQuery);

export const useGetMenuObjectLazyQuery = () => {
  const [fetchCombo] = useComboQuery();
  const [fetchItem] = useItemQuery();

  const client = useApolloClient();
  const uri = useSanityGqlEndpoint();
  const { vendor: posVendor } = usePosVendor();

  const getQueryFn = useCallback(
    (type: string) => {
      if (type === MenuObjectTypes.COMBO) {
        return fetchCombo;
      }
      if (type === MenuObjectTypes.ITEM) {
        return fetchItem;
      }
      return null;
    },
    [fetchCombo, fetchItem]
  );

  const getVariantContentOptions = useGetVariantContentOptions();

  return useCallback(
    async (menuObjectId: string) => {
      const [type, sanityId] = menuObjectId.split(/-(.*)/);

      if (type === MenuObjectTypes.PICKER) {
        if (!posVendor) {
          return null;
        }

        const vendorVariables = buildVendorVariables(posVendor);
        try {
          const data = await fetchPickerBatchedAsync(client, uri, sanityId, vendorVariables);
          if (!data) {
            return null;
          }
          const menuObject = getValidDataFromQuery(
            data as IGetPickerPlusByVendorQuery,
            getVariantContentOptions
          );
          return menuObject ?? null;
        } catch (e) {
          logger.warn({
            sanityId,
            error: e,
            message: '[useGetMenuObjectLazyQuery] Batched picker fetch failed',
          });
          return null;
        }
      }

      const queryFn = getQueryFn(type);

      if (!queryFn) {
        return Promise.resolve(null);
      }

      return new Promise<IMenuObject | null>((resolve, reject) => {
        queryFn({
          variables: { id: sanityId },
          allowFromCache: true,
          onCompleted: (data: IGetComboPlusQuery | IGetItemPlusQuery | IGetPickerPlusQuery) => {
            const menuObject = getValidDataFromQuery(data, getVariantContentOptions);
            if (menuObject) {
              resolve(menuObject);
            } else {
              reject(null);
            }
          },
          onError: () => {
            reject(null);
          },
        });
      });
    },
    [getQueryFn, getVariantContentOptions, client, uri, posVendor]
  );
};
